/**
 * AdminLoginPage — dedicated login at /admin/login for super_admin / admin.
 *
 * Visually distinct from the main /login page (dark background, amber accent)
 * to make it obvious this is the staff entrance, NOT the student login.
 * On success it redirects straight to /admin (the CMS panel).
 *
 * If a regular student/instructor logs in here, we sign them out and show an
 * error — keeps the staff entrance free of LMS traffic.
 */
import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, ArrowLeft, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

const SBL_LOGO = "/sbl-logo.jpeg";

export default function AdminLoginPage() {
  const { user, login, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <div className="min-h-screen bg-slate-950 text-white grid place-items-center text-sm">Loading…</div>;
  if (user && ["super_admin", "admin"].includes(user.role)) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await login(email, password);
      if (!res?.success) {
        toast.error(res?.error || "Invalid credentials");
        setSubmitting(false);
        return;
      }
      const u = res.data;
      if (!["super_admin", "admin"].includes(u.role)) {
        await logout();
        toast.error("This entrance is reserved for site administrators. Use the regular /login if you are a student or instructor.");
        setSubmitting(false);
        return;
      }
      toast.success("Welcome back");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #c9a227 0%, transparent 40%), radial-gradient(circle at 80% 70%, #2563eb 0%, transparent 35%)" }} />
      <div className="relative max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white text-xs">
          <ArrowLeft size={14} /> Public site
        </Link>
        <Link to="/login" className="text-xs text-white/70 hover:text-white" data-testid="admin-login-go-regular">
          Student / Instructor login &rarr;
        </Link>
      </div>

      <div className="relative max-w-md mx-auto px-6 py-8">
        <div className="text-center mb-6">
          <img src={SBL_LOGO} alt="SBL" className="w-14 h-14 mx-auto rounded-sm shadow-lg" />
          <p className="mt-4 text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold">Site Admin Entrance</p>
          <h1 className="text-3xl font-bold mt-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Public-Site CMS</h1>
          <p className="text-sm text-white/60 mt-2">
            Sign in to manage your public website (landing copy, programme catalogue, gallery).
          </p>
        </div>

        <Card className="bg-white/5 border border-white/10 backdrop-blur-md rounded-none">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-amber-400 font-bold">Admin email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@yourschool.edu"
                  className="bg-slate-900/60 border-white/10 text-white placeholder:text-white/30 rounded-none mt-1"
                  data-testid="admin-login-email"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-widest text-amber-400 font-bold">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-slate-900/60 border-white/10 text-white placeholder:text-white/30 rounded-none mt-1"
                  data-testid="admin-login-password"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-amber-400 hover:bg-amber-500 text-slate-950 rounded-none font-bold gap-2"
                data-testid="admin-login-submit"
              >
                <Lock size={14} weight="fill" /> {submitting ? "Signing in…" : "Sign in to CMS"}
              </Button>
            </form>
            <div className="mt-5 pt-5 border-t border-white/10 text-center">
              <Link to="/forgot-password" className="text-xs text-white/60 hover:text-amber-400" data-testid="admin-login-forgot">
                Forgot password?
              </Link>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-[11px] text-white/40 flex items-center justify-center gap-1">
          <ShieldCheck size={12} /> Restricted area · Authorised personnel only
        </div>
      </div>
    </div>
  );
}
